import React, { useState, useRef } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useLanguage, translateDynamic } from '../../context/LanguageContext';
import { transactionService } from '../../services/api';
import tenantStorage from '../../services/tenantStorage';
import { createItemsWithAddons } from '../../utils/transactionLines';
import { splitSubtotal, splitLineTotal } from '../../utils/splitBillTotals';
// A split bill is the one charge path that never reaches printSaleReceipts —
// transactionFlow.js hands "Split Payment" to this component and returns —
// so the drawer has to be fired from here or not at all. Every transaction
// created below is offered to the server, which decides per transaction
// whether it qualifies (services/drawer_policy.py). There is deliberately no
// policy check on this side to drift out of step with it, and no attempt to
// pick "the one that took the cash": a drawer already open being asked to
// open again is harmless, a shut drawer with a customer waiting is not.
import { kickDrawerForSale } from '../../utils/drawerKick';
// The kitchen's copy of a split sale. Same reason as the drawer above: the
// charge path returns at "Split Payment" BEFORE its own
// dispatchKitchenTickets call, so a split's order-tracking records — the rows
// the KDS screen reads and the rows a network ticket renders from — have to
// be created here or not at all. The function itself is REUSED, not copied,
// so the split path and the normal path cannot drift apart.
//
// linkInventoryToTransaction is the shelf's copy of exactly the same story:
// the charge path returns at "Split Payment" BEFORE its own call to it, so a
// split's ingredient deduction has to be fired here or not at all. REUSED
// for the same reason — a split sale and an ordinary one must take stock off
// by the same rules. See linkSplitInventory.
import {
    dispatchKitchenTickets,
    linkInventoryToTransaction,
} from '../../pos-app/surfaces/cashier/lib/transactionFlow';
import Modal from '../common/Modal';
import {
    printSplitBillReceipts,
    generateReceiptHTML,
    printCategoryOrdersOnly,
    printWithIframe
} from '../../utils/PrintingUtils';

const SplitBillManager = React.forwardRef(({
    // Props from parent
    pendingTransaction,
    setPendingTransaction,
    currentConfirmOrder,
    setCurrentConfirmOrder,
    pendingOrders,
    setPendingOrders,
    printedOrders,
    setPrintedOrders,
    pendingOrderIds,
    cashierName,
    selectedDiningOption,
    selectedPaymentMethod,
    tableNumber,
    notes,
    selectedProducts,
    setSelectedProducts,
    currency,
    paymentMethods,
    tenantConfig,
    printableCategories,
    brandName,
    tenantId,
    handleCalculateTotals,
    loadTransactions,
    setSuccess,
    setError,
    setLoading,
    loading,
    setNotes,
    setDiscountAmount,
    setPayableAmount,
    setSelectedCategory,
    setSelectedSubcategory,
    setSelectedDiningOption,
    setTableNumber,
    setAddServiceCharge,
    setDiscountType,
    setDiscountPercentage,
    setDiscountMode,
    // Callbacks
    onSplitCompleted // Called when split is completed
}, ref) => {
    const { t } = useLanguage();

    // Split Bill State
    // Re-entrancy guard for every path in this file that creates a
    // transaction.
    //
    // PaymentConfirmModal was guarded after production showed duplicate
    // charges — same amount, same method, 0.4-4.3s apart, some triples.
    // That guard does NOT cover this component: transactionFlow.js hands
    // "Split Payment" straight here and returns, and the buttons below call
    // createTransaction down five separate paths of their own.
    //
    // A ref, not state, because it has to be SYNCHRONOUS. `loading` is a
    // prop set inside each handler, so two taps landing in the same tick
    // both read the pre-render value and both charge. A ref flips before the
    // second tap is dispatched. The state mirror exists only so the buttons
    // can render as disabled.
    const chargingRef = useRef(false);
    const [charging, setCharging] = useState(false);

    // Tax is not a column on transactions — it only ever lands in
    // final_amount, while total_amount stays the subtotal. Every split-by-
    // product path built its totals as subtotal + service charge and never
    // added it, so splitting a taxed order under-charged by the entire tax
    // amount: £27.00 taken on a £34.56 bill in QC on 2026-09-08.
    //
    // Mirrors calculateTotals in utils/OrderManagement — tax applies only
    // when the order's dining option is in the tenant's tax_applied list.
    const splitTaxRate = () => {
        const diningOption = transactionToSplit?.dining_option || selectedDiningOption;
        const applied = tenantConfig?.tax_applied || [];
        return applied.includes(diningOption) ? (tenantConfig?.tax_rate || 0) : 0;
    };

    // Totals for a SUBSET of an order's products. serviceChargeApplies comes
    // from whichever record owns the split (the pending basket or the
    // transaction being split), matching what each caller already used.
    const splitTotals = (products, serviceChargeApplies) => {
        const subtotal = splitSubtotal(products);
        const serviceCharge = serviceChargeApplies
            ? subtotal * (tenantConfig?.service_charge_rate || 0.12)
            : 0;
        const tax = subtotal * splitTaxRate();
        return { subtotal, serviceCharge, tax, total: subtotal + serviceCharge + tax };
    };

    // How a set of paid-for products divides across payment methods.
    //
    // Add-ons and tax are included proportionally: final_amount is derived
    // from splitSubtotal (which counts add-ons) plus service charge and tax,
    // so a bare selling_price here would leave cash + card summing to LESS
    // than the amount actually charged. The drawer would not reconcile and
    // payment-method revenue would under-count every add-on sold on a split.
    const paymentTotalsFor = (products) => {
        const totals = { cash: 0, card: 0, mobile: 0, giftCard: 0 };
        const serviceChargeRate = tenantConfig?.service_charge_rate || 0.12;

        products.forEach((product) => {
            const productSubtotal = splitLineTotal(product);
            const productServiceCharge = transactionToSplit?.service_charge
                ? productSubtotal * serviceChargeRate
                : 0;
            const productTax = productSubtotal * splitTaxRate();
            const productTotal = productSubtotal + productServiceCharge + productTax;

            if (product.paymentMethod === 'Cash') totals.cash += productTotal;
            else if (product.paymentMethod === 'Card') totals.card += productTotal;
            else if (product.paymentMethod === 'Mobile') totals.mobile += productTotal;
            else if (product.paymentMethod === 'Gift Card') totals.giftCard += productTotal;
        });

        return totals;
    };

    // The sale a split's groups belong to.
    //
    // Each group is paid separately and wants a receipt at that moment, and
    // a receipt renders from a transaction row — so the parent is created
    // when the first group pays, and every group after it becomes a child
    // (TXABC_1, TXABC_2). The parent holds the money; children are excluded
    // from every revenue aggregation.
    //
    // A ref, not state: two groups settled in quick succession must not each
    // create their own parent, and state would not have re-rendered in time.
    const splitParentRef = useRef(null);

    // Re-entrancy guard for the per-group path.
    //
    // Deliberately NOT beginCharge: this handler delegates to the guarded
    // final handler, and holding that guard would make the delegated call
    // refuse itself. Same reasoning as beginCharge otherwise — a ref, so two
    // taps in one frame cannot both get through.
    const groupChargeRef = useRef(false);

    // The same guard for the per-CUSTOMER path, which creates rows of its own.
    // Its own ref rather than a shared one: the two paths are separate flows
    // and a shared guard would let a bug in one strand the other.
    const customerChargeRef = useRef(false);

    // One set of kitchen/bar tickets per SALE, not per payer.
    //
    // Deliberately redundant TODAY, and verified as such: removing this line
    // leaves every test in SplitBillManager.kitchenTickets.test.js green,
    // because ensureSplitParent already early-returns on splitParentRef and
    // so runs its body once per split however many people pay.
    //
    // It is kept because that is an accident of a DIFFERENT function's
    // contract. splitParentRef exists to stop a second parent TRANSACTION
    // being opened; nothing about it promises anything to the kitchen. Make
    // ensureSplitParent retry after a failed create — an entirely reasonable
    // future change — and the kitchen silently gets every docket twice, which
    // is the one regression this fix can cause and the hardest to notice from
    // the till. The invariant that matters here is stated here.
    //
    // A ref, not state, for the same reason splitParentRef is one: two payers
    // settled in quick succession must not each get through.
    //
    // Reset at each of the three split ENTRY points alongside splitParentRef,
    // never at the end: a split abandoned halfway still ends by starting the
    // next one, and a guard left set would silently stop sending orders to
    // the kitchen for the rest of the shift.
    const ticketsDispatchedRef = useRef(false);

    // One recipe deduction per SALE, not per payer. Same shape and same
    // reasoning as ticketsDispatchedRef above — see linkSplitInventory.
    const inventoryLinkedRef = useRef(false);

    // Whether this sale's stock has already left the shelf.
    //
    // `currentConfirmOrder` means a PENDING order is being settled, and
    // holding an order calls OrderHelpers.deductStockForPendingOrder, which
    // for a manual-stock product writes products.quantity straight down. The
    // backend decrements again for every PARENT transaction it receives
    // (children are already exempt), so without this a split-paid table
    // order takes the same unit off twice: BAC001 "Rice and Meat" went
    // 9 -> 6 across two sales of one unit each on depos_dev during the
    // 9 Sep 2026 QC, and TXCF255DD2 — table 4, held at 20:34, split-paid at
    // 20:37 — is one of those decrements.
    //
    // Deliberately the SAME signal dispatchSplitKitchenTickets gates on, for
    // the same reason: both are asking "did this order already happen once,
    // before it was paid for?".
    //
    // Applied to all FIVE creates this component can open a sale through,
    // rather than inherited from `pendingTransaction` — three of them build
    // their parent from scratch and would silently miss it.
    const stockAlreadyDeducted = () => !!currentConfirmOrder;

    // Send a split sale's order to the kitchen, exactly once.
    //
    // `currentConfirmOrder` means this is a pending table order being
    // charged: pendingOrderFlow already created its tracking records and
    // fired its dockets at send-to-kitchen time, so dispatching here would
    // print every kitchen docket twice. That is the same test
    // dispatchKitchenTickets itself uses to tell an immediate sale from a
    // pending one — mirrored rather than re-invented.
    //
    // Awaited: dispatchKitchenTickets creates the order-tracking rows (what
    // the KDS reads) and awaits them, and only DETACHES the paper via
    // startTicketPrint. It swallows every error of its own, so awaiting it
    // can neither fail nor delay this sale beyond the tracking POST that the
    // ordinary non-split charge path already waits on.
    const dispatchSplitKitchenTickets = async (parentTransactionId, products) => {
        if (currentConfirmOrder) return;
        if (ticketsDispatchedRef.current) return;
        if (!parentTransactionId) return;
        ticketsDispatchedRef.current = true;

        await dispatchKitchenTickets({
            // Explicitly the immediate-sale branch — the pending-order branch
            // is unreachable here, having just been returned on above.
            currentConfirmOrder: null,
            selectedProducts: products || [],
            printableCategories,
            selectedDiningOption,
            cashierName,
            notes,
            // The SALE's id. A child row holds no money and is excluded from
            // every gold aggregation, so a ticket filed against one would not
            // join back to the sale it belongs to.
            transactionId: parentTransactionId,
            // Same identifier the split's parent transaction carries (line 277)
            // and the same one its receipts print — a split takeaway's kitchen
            // ticket now reads "Order: #019" like the non-split path's.
            tableNumber,
            pendingOrders,
            tenantConfig,
        });
    };

    // Take a split sale's INGREDIENTS off the shelf, exactly once.
    //
    // Recipe (auto-stock) deduction is driven from the client: the ordinary
    // charge path calls this same function after its sale is created, and it
    // is what writes the inventory_transactions rows a product's recipe
    // implies. transactionFlow hands "Split Payment" to this component and
    // RETURNS — before that call — and this component had no inventory call
    // of any kind, so every split sale ever recorded sold its ingredients for
    // free: on depos_dev every split back to June 2025 has zero rows matching
    // its id, while every ordinary sale beside it has two to four. The hole
    // predates the split-children stock fix (#180) entirely; that one only
    // ever touched products.quantity, which is the MANUAL half.
    //
    // Fired on the PARENT and guarded by a ref, so an N-way split deducts
    // once rather than N+1 times — the very double-decrement #180 closed on
    // the manual side. Children are refused stock server-side and must stay
    // that way: they hold no money and are excluded from every gold
    // aggregation, so ingredients booked against one would not join back to
    // the sale.
    //
    // A HELD order deducted at send-to-kitchen time, booked against the table
    // key; linkInventoryToTransaction's `currentConfirmOrder` branch relabels
    // those rows onto the sale instead of deducting again. Reused rather than
    // re-implemented, exactly as dispatchKitchenTickets is.
    //
    // Never throws: the money is already taken by the time this runs, and no
    // stock-keeping failure may strand a cashier mid-split.
    const linkSplitInventory = async (parentTransactionId, products) => {
        if (inventoryLinkedRef.current) return;
        if (!parentTransactionId) return;
        inventoryLinkedRef.current = true;

        try {
            await linkInventoryToTransaction({
                products: products || [],
                transactionId: parentTransactionId,
                currentConfirmOrder,
                selectedDiningOption,
                cashierName,
            });
        } catch (err) {
            console.error('Error linking inventory to split transaction:', err);
        }
    };

    // `splitType` is the sale's own kind ('product' or 'table'), NOT the
    // group's — both kinds open their sale the same way and differ only in
    // how the bill is divided. `total`, `notes` and `items` are supplied by
    // the caller because a product split's parent is priced off the products
    // still unpaid, while a customer split's is the bill it was opened from.
    const ensureSplitParent = async ({ splitType, total, notes, items, products }) => {
        if (splitParentRef.current) return splitParentRef.current;

        const parent = await transactionService.createTransaction({
            transaction_id: '',
            parent_transaction_id: null,
            split_type: splitType,
            date: format(new Date(), 'yyyy-MM-dd'),
            time: format(new Date(), 'HH:mm:ss'),
            sales_date: await tenantStorage.getState('salesDate') || format(new Date(), 'yyyy-MM-dd'),
            staff_name: cashierName,
            payment_method: 'Split Payment',
            dining_option: selectedDiningOption,
            table_number: tableNumber,
            service_charge: transactionToSplit?.service_charge,
            discount_amount: 0,
            total_amount: total,
            final_amount: total,
            // The payment mix is not known until the last group has paid;
            // setSplitTotals fills it in then.
            cash_amount: 0,
            card_amount: 0,
            notes,
            items,
            products,
            tenant_id: tenantId,
            // See stockAlreadyDeducted.
            stock_already_deducted: stockAlreadyDeducted(),
        });

        splitParentRef.current = parent?.transaction_id || null;

        // The sale now exists, so the kitchen's copy of it can be filed
        // against something. This is the single point BOTH split-by-product
        // and split-by-customer open their sale through, which is why the
        // dispatch sits here rather than in each of them.
        await dispatchSplitKitchenTickets(splitParentRef.current, products);

        // …and so can the shelf's. `products` is the WHOLE bill here — the
        // sale owes every ingredient on it, not the group that paid first.
        await linkSplitInventory(splitParentRef.current, products);

        return splitParentRef.current;
    };

    // The sale a split-by-PRODUCT's groups belong to.
    const ensureProductSplitParent = () => {
        const allProducts = transactionToSplit?.products || [];
        const { total } = splitTotals(allProducts, transactionToSplit?.service_charge);
        return ensureSplitParent({
            splitType: 'product',
            total,
            notes: t('split_by_products_count', { count: allProducts.length }),
            items: createItemsWithAddons(allProducts),
            products: allProducts,
        });
    };

    // The sale a split-by-CUSTOMER's payers belong to. Priced off the bill
    // being split, which is what every payer's share is derived from.
    const ensureCustomerSplitParent = () => ensureSplitParent({
        splitType: 'table',
        total: parseFloat(transactionToSplit?.final_amount) || 0,
        notes: t('split_by_table_ways', { ways: numberOfCustomers }),
        items: transactionToSplit?.items,
        products: transactionToSplit?.products || [],
    });

    // Returns false when a charge is already in flight — callers bail.
    const beginCharge = () => {
        if (chargingRef.current) return false;
        chargingRef.current = true;
        setCharging(true);
        return true;
    };

    // Re-arm. Called on every failure path so a customer standing at the
    // till can be charged again; NOT called on success, where the modal
    // closes and the component's own reset runs.
    const endCharge = () => {
        chargingRef.current = false;
        setCharging(false);
    };

    const [splitBillModalOpen, setSplitBillModalOpen] = useState(false);
    const [transactionToSplit, setTransactionToSplit] = useState(null);
    const [splitByNumberModalOpen, setSplitByNumberModalOpen] = useState(false);
    const [numberOfCustomers, setNumberOfCustomers] = useState(2);
    const [selectedProductsForSplit, setSelectedProductsForSplit] = useState([]);
    const [splitMethod, setSplitMethod] = useState('equal');
    const [splitPaymentModalOpen, setSplitPaymentModalOpen] = useState(false);
    // The cash/card inputs hold the raw string the cashier typed so the
    // field can be emptied (see their onChange). Everything that reads them
    // goes through this: an empty or half-typed field is worth 0, never NaN,
    // which would otherwise render as "£NaN" and be stored on the transaction.
    const splitAmountValue = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
    };

    const [cashAmount, setCashAmount] = useState(0);
    const [cardAmount, setCardAmount] = useState(0);
    const [productSplitPaymentModalOpen, setProductSplitPaymentModalOpen] = useState(false);
    const [currentProductForSplit, setCurrentProductForSplit] = useState(null);
    const [currentProductPaymentMethod, setCurrentProductPaymentMethod] = useState('Cash');
    const [allProcessedProducts, setAllProcessedProducts] = useState([]);
    const [splitTypeModalOpen, setSplitTypeModalOpen] = useState(false);
    const [selectedModalPaymentMethod, setSelectedModalPaymentMethod] = useState(null);
    const [splitCustomerPaymentModalOpen, setSplitCustomerPaymentModalOpen] = useState(false);
    const [currentSplitCustomer, setCurrentSplitCustomer] = useState(1);
    const [customerPayments, setCustomerPayments] = useState([]);
    const [currentCustomerPaymentMethod, setCurrentCustomerPaymentMethod] = useState('Cash');

    // Split handling functions
    const handleSplitByProducts = (transaction) => {
        setTransactionToSplit(transaction);
        setSplitMethod('byProduct');

        // **START WITH EMPTY SELECTION AND RESET ACCUMULATED PRODUCTS**
        setSelectedProductsForSplit([]);
        setAllProcessedProducts([]); // ADD THIS LINE
        // A new bill is a new sale. Without this the next split's groups
        // would be recorded as children of the previous one — and its order
        // would never reach the kitchen.
        splitParentRef.current = null;
        ticketsDispatchedRef.current = false;
        // …and it owes its own stock. A guard left set would silently stop
        // deducting for the rest of the shift.
        inventoryLinkedRef.current = false;

        setSplitBillModalOpen(true);
    };

    const handleSplitByNumber = (transaction) => {
        setTransactionToSplit(transaction);
        setSplitMethod('equal');
        // A new bill is a new sale. Without this the next split's customers
        // would be recorded as children of the previous one — the same reset
        // handleSplitByProducts makes above, for the same reason.
        splitParentRef.current = null;
        ticketsDispatchedRef.current = false;
        inventoryLinkedRef.current = false;
        setSplitByNumberModalOpen(true);
    };

    const handleSplitByMethod = () => {
        // This path opens its sale inline rather than through
        // ensureSplitParent, but it is still a new sale and still owes the
        // kitchen one set of tickets — see handleSplitPaymentSubmit — and one
        // deduction.
        ticketsDispatchedRef.current = false;
        inventoryLinkedRef.current = false;
        setSplitPaymentModalOpen(true);
    };

    const showSplitTypeModal = () => {
        setSplitTypeModalOpen(true);
    };

    const toggleProductForSplit = (individualProduct) => {
        setSelectedProductsForSplit(prev => {
            const isSelected = prev.some(p => p.uniqueId === individualProduct.uniqueId);

            if (isSelected) {
                // Remove from selection
                return prev.filter(p => p.uniqueId !== individualProduct.uniqueId);
            } else {
                // Add to selection
                return [...prev, individualProduct];
            }
        });
    };

    const processSplitByProducts = async () => {
        // One charge at a time — see beginCharge.
        if (!beginCharge()) return;
        try {
            setLoading(true);

            const parentTransaction = {
                ...pendingTransaction,
                payment_method: 'Split Payment',
                notes: `${pendingTransaction.notes ? pendingTransaction.notes + ' - ' : ''}Parent transaction for split by product`,
                // See stockAlreadyDeducted. Set explicitly rather than left
                // to the spread, so all five creates state it the same way.
                stock_already_deducted: stockAlreadyDeducted(),
            };

            const parentResult = await transactionService.createTransaction(parentTransaction);
            const parentTransactionId = parentResult.transaction_id;
            kickDrawerForSale(parentTransactionId).catch(() => {});

            // A sale of its own, so it owes its own stock. Currently
            // unreachable — nothing in the JSX or the imperative handle calls
            // this handler — but it is one of the five creates that can open
            // a sale, and a create that opens a sale without deducting is the
            // exact hole this change closes.
            await linkSplitInventory(parentTransactionId, selectedProducts);

            // Add-ons are part of what the customer owes — see
            // utils/splitBillTotals.js for what this used to omit.
            const { subtotal, serviceCharge, total: splitTotal } =
                splitTotals(selectedProductsForSplit, pendingTransaction.service_charge);

            const receiptData = {
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                cashier: cashierName,
                paymentMethod: selectedPaymentMethod,
                diningOption: selectedDiningOption,
                tableNumber: tableNumber,
                products: selectedProductsForSplit,
                subtotal,
                discount: 0,
                serviceCharge,
                notes: t('split_from_transaction', { id: parentTransactionId }),
                splitInfo: {
                    splitNumber: 1,
                    totalSplits: 1,
                    amountPerSplit: splitTotal
                }
            };

            receiptData.total = receiptData.subtotal + receiptData.serviceCharge;

            // The shared builder — NOT a fifth inline map. It emits one row
            // per line (parent before child) carrying line_id/parent_line_id/
            // group_id, which is what the server validates modifiers against
            // and what gold.product_performance reads. The old inline map
            // emitted no add-on rows at all.
            const items = createItemsWithAddons(selectedProductsForSplit);

            const transaction = {
                transaction_id: '',
                parent_transaction_id: parentTransactionId,
                split_type: 'product',
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                sales_date: await tenantStorage.getState('salesDate') || format(new Date(), 'yyyy-MM-dd'),
                staff_name: cashierName,
                payment_method: selectedPaymentMethod,
                dining_option: selectedDiningOption,
                table_number: tableNumber,
                service_charge: pendingTransaction.service_charge,
                discount_amount: 0,
                total_amount: receiptData.subtotal,
                final_amount: receiptData.total,
                notes: t('split_from_transaction', { id: parentTransactionId }),
                items: items,
                products: selectedProductsForSplit
            };

            const childResult = await transactionService.createTransaction(transaction);
            kickDrawerForSale(childResult?.transaction_id).catch(() => {});

            const tenantInfo = {
                brand_name: brandName,
                currency,
                printable_categories: printableCategories,
                printing: tenantConfig?.printing,
                receipt_header: tenantConfig?.receipt_header,
                receipt_footer: tenantConfig?.receipt_footer,
                receipt_logo: tenantConfig?.receipt_logo,
                // A network Epson/ESC-POS printer renders the receipt from the
                // transaction itself, so the direct print path needs the id —
                // without it attemptDirectEpsonPrint bails and NOTHING prints.
                transaction_id: childResult?.transaction_id
            };

            // tenantInfo was being passed in the fullBillData slot, so
            // printSplitBillReceipts received tenantInfo=null and could not
            // even read the tenant's printing method.
            printSplitBillReceipts(receiptData, "SPLIT BY PRODUCT", false, null, tenantInfo);

            if (transactionToSplit && transactionToSplit.products) {
                const selectedIds = selectedProductsForSplit.map(p => p.id);
                const remainingProducts = transactionToSplit.products.filter(p => !selectedIds.includes(p.id));

                if (remainingProducts.length === 0) {
                    await loadTransactions();
                    setSplitBillModalOpen(false);
                    setTransactionToSplit(null);
                    setSelectedProductsForSplit([]);
                    setSuccess(t('split_bill_completed_successfully'));
                } else {
                    setTransactionToSplit({
                        ...transactionToSplit,
                        products: remainingProducts
                    });
                    setSelectedProductsForSplit([]);
                    setSuccess(t('split_processed_select_more_or_finish'));
                }
            }

            setLoading(false);
            endCharge();

            setTimeout(() => {
                setSuccess('');
            }, 1000);

        } catch (err) {
            setError(t('failed_to_process_split_bill'));
            console.error(err);
            setLoading(false);
            endCharge();
        }
    };

    const processIndividualProductPayment = async () => {
        // One group at a time. This path creates real transaction rows now,
        // so a double tap would record the same group twice.
        if (groupChargeRef.current) return;
        groupChargeRef.current = true;
        try {
            if (selectedProductsForSplit.length === 0) return;

            if (allProcessedProducts.length === 0) { // First payment
                const diningConfig = tenantConfig?.dining_options?.[selectedDiningOption];
                const isTableBasedOrder = diningConfig !== undefined && diningConfig !== "";

                if (!isTableBasedOrder) { // Only print for takeaway/non-table orders
                    const categoryReceiptData = {
                        date: format(new Date(), 'yyyy-MM-dd'),
                        time: format(new Date(), 'HH:mm:ss'),
                        cashier: cashierName,
                        paymentMethod: 'Split Payment - By Product',
                        diningOption: selectedDiningOption,
                        tableNumber: tableNumber,
                        notes: notes || ''
                    };

                    const splitPaymentTenantInfo = {
                        brand_name: brandName,
                        currency,
                        printable_categories: printableCategories,
                        printing: tenantConfig?.printing,
                        receipt_header: tenantConfig?.receipt_header,
                        receipt_footer: tenantConfig?.receipt_footer,
                        receipt_logo: tenantConfig?.receipt_logo
                    };

                    // Print category orders for ALL products in the original transaction
                    const allProducts = transactionToSplit.products || [];
                    const categoryOrdersPrinted = printCategoryOrdersOnly(
                        categoryReceiptData,
                        allProducts,
                        splitPaymentTenantInfo
                    );

                    if (categoryOrdersPrinted) {
                        console.log('Category orders printed for product split payment (takeaway)');
                    }
                } else {
                    console.log('Skipping category orders - table order already has them printed');
                }
            }

            // Add payment method to all selected products
            const processedProducts = selectedProductsForSplit.map(product => ({
                ...product,
                paymentMethod: currentProductPaymentMethod
            }));

            // **ADD TO ACCUMULATED PROCESSED PRODUCTS**
            setAllProcessedProducts(prev => [...prev, ...processedProducts]);

            // This previously summed the base price alone — no add-ons AND no
            // quantity multiplier, so a split of two of anything charged for one.
            const { subtotal: subtotalAmount, serviceCharge: serviceChargeAmount, total: totalAmount } =
                splitTotals(processedProducts, transactionToSplit.service_charge);

            // **PRINT CUSTOMER RECEIPT IMMEDIATELY**
            //
            // This group becomes a child row of the sale before its slip prints.
            // Receipts render from a transaction, so before this existed there
            // was nothing to render and a network printer produced no per-group
            // slip at all — the modal's promise of "a customer receipt" per group
            // was only ever true in a browser.
            let childTransactionId = null;
            try {
                const parentId = await ensureProductSplitParent();
                const child = await transactionService.createTransaction({
                    transaction_id: '',
                    parent_transaction_id: parentId,
                    split_type: 'product',
                    date: format(new Date(), 'yyyy-MM-dd'),
                    time: format(new Date(), 'HH:mm:ss'),
                    sales_date: await tenantStorage.getState('salesDate') || format(new Date(), 'yyyy-MM-dd'),
                    staff_name: cashierName,
                    payment_method: currentProductPaymentMethod,
                    dining_option: selectedDiningOption,
                    table_number: tableNumber,
                    service_charge: transactionToSplit.service_charge,
                    discount_amount: 0,
                    total_amount: totalAmount,
                    final_amount: totalAmount,
                    notes: t('split_by_products_detail_items', { items: processedProducts.map(p => p.name).join(', ') }),
                    items: createItemsWithAddons(processedProducts),
                    products: processedProducts,
                    tenant_id: tenantId,
                });
                childTransactionId = child?.transaction_id || null;

                // Never awaited, and never downstream of anything that can
                // throw. kickDrawerForSale swallows its own failures, so it
                // cannot take the reconciliation below down — but the reverse
                // was true while it sat underneath: setSplitTotals rejects on
                // network failure for any leg, which left a cashier holding
                // cash at a shut till.
                kickDrawerForSale(childTransactionId).catch(() => {});

                // Keep the sale's payment mix true after every group, not
                // only at the end. A split abandoned halfway would otherwise
                // leave a full-bill sale on record with nothing attributed to
                // cash or card — revenue that reconciles against neither the
                // drawer nor the terminal.
                const paidSoFar = paymentTotalsFor([...allProcessedProducts, ...processedProducts]);
                await transactionService.setSplitTotals(parentId, {
                    cashAmount: paidSoFar.cash,
                    cardAmount: paidSoFar.card,
                });
            } catch (err) {
                // The group has already been paid for in the real world. Losing
                // its slip is bad; refusing to continue the split would be worse,
                // and the sale is still recorded on the parent at the end.
                console.error('Could not record this split group, slip will not print:', err);
            }

            const tenantInfo = {
                brand_name: brandName,
                currency,
                printable_categories: printableCategories,
                printing: tenantConfig?.printing,
                receipt_header: tenantConfig?.receipt_header,
                receipt_footer: tenantConfig?.receipt_footer,
                receipt_logo: tenantConfig?.receipt_logo,
                transaction_id: childTransactionId
            };

            const receiptData = {
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                cashier: cashierName,
                paymentMethod: currentProductPaymentMethod,
                diningOption: selectedDiningOption,
                tableNumber: tableNumber,
                products: processedProducts,
                subtotal: subtotalAmount,
                discount: 0,
                serviceCharge: serviceChargeAmount,
                total: totalAmount,
                notes: t('split_by_products_detail_items', { items: processedProducts.map(p => p.name).join(', ') })
            };

            console.log(`Printing receipt for ${processedProducts.length} selected products`);
            printSplitBillReceipts(
                receiptData,
                `PRODUCT SPLIT - CUSTOMER RECEIPT`,
                false,
                null,
                tenantInfo
            );

            // Remove processed products from transaction
            if (transactionToSplit.products) {
                // Update the original products by reducing quantities
                const updatedProducts = transactionToSplit.products.map(product => {
                    const processedCount = processedProducts.filter(p => p.product_code === product.product_code).length;
                    return {
                        ...product,
                        quantity: product.quantity - processedCount
                    };
                }).filter(product => product.quantity > 0);

                setTransactionToSplit(prev => ({
                    ...prev,
                    products: updatedProducts
                }));

                // **CHECK IF ALL PRODUCTS ARE PROCESSED USING ACCUMULATED COUNT**
                const totalOriginalCount = transactionToSplit.products?.reduce((sum, p) => sum + p.quantity, 0) || 0;
                const totalProcessedCount = allProcessedProducts.length + processedProducts.length;
                console.log(`Total original: ${totalOriginalCount}, Total processed: ${totalProcessedCount}`);

                if (updatedProducts.length === 0) {
                    // All products processed, create final transaction with ALL processed products
                    const finalProcessedProducts = [...allProcessedProducts, ...processedProducts];
                    setTimeout(() => {
                        processFinalProductSplitTransaction(finalProcessedProducts);
                    }, 1000);
                }
            }

                // Reset selection
                setSelectedProductsForSplit([]);
                setProductSplitPaymentModalOpen(false);
                setCurrentProductPaymentMethod('Cash');
        } finally {
            // Re-armed unconditionally: the next group still has to be paid
            // for, and a stuck guard would strand the rest of the split.
            groupChargeRef.current = false;
        }
    };

    const processFinalProductSplitTransaction = async (allProcessedProducts) => {
        // One charge at a time — see beginCharge.
        if (!beginCharge()) return;
        try {
            setLoading(true);
            console.log('Processing final product split transaction with products:', allProcessedProducts);
            console.log('Payment method breakdown:', allProcessedProducts.map(p => ({ name: p.name, method: p.paymentMethod })));

            const { subtotal: subtotalAmount, serviceCharge: serviceChargeAmount, total: totalAmount } =
                splitTotals(allProcessedProducts, transactionToSplit.service_charge);

            // Calculate payment method totals (including proportional
            // service charge and tax) — see paymentTotalsFor.
            const {
                cash: totalCashAmount,
                card: totalCardAmount,
                mobile: totalMobileAmount,
                giftCard: totalGiftCardAmount,
            } = paymentTotalsFor(allProcessedProducts);

            const transaction = {
                transaction_id: '',
                parent_transaction_id: null,
                split_type: 'product',
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                sales_date: await tenantStorage.getState('salesDate') || format(new Date(), 'yyyy-MM-dd'),
                staff_name: cashierName,
                payment_method: 'Split Payment',
                dining_option: selectedDiningOption,
                table_number: tableNumber,
                service_charge: transactionToSplit.service_charge,
                discount_amount: 0,
                total_amount: totalAmount,
                final_amount: totalAmount,
                notes: t('split_by_products_count', { count: allProcessedProducts.length }),
                items: createItemsWithAddons(allProcessedProducts),
                products: allProcessedProducts,
                cash_amount: totalCashAmount,
                card_amount: totalCardAmount,
                mobile_amount: totalMobileAmount,
                gift_card_amount: totalGiftCardAmount,
                tenant_id: tenantId,
                // See stockAlreadyDeducted.
                stock_already_deducted: stockAlreadyDeducted(),
            };

            // The sale already exists: ensureSplitParent created it when the
            // first group paid, so its children had something to hang off.
            // Finalising it means recording how it was paid, not opening a
            // second sale for the same money.
            let result;
            if (splitParentRef.current) {
                await transactionService.setSplitTotals(splitParentRef.current, {
                    cashAmount: totalCashAmount,
                    cardAmount: totalCardAmount,
                });
                result = { transaction_id: splitParentRef.current };
                console.log('Split finalised on parent:', result.transaction_id);
            } else {
                // No parent means no group was recorded — the split was
                // completed in one go, or every child creation failed. Either
                // way the sale still has to be recorded.
                console.log('Creating final product split transaction:', transaction);
                result = await transactionService.createTransaction(transaction);
                console.log('Transaction created:', result);
                kickDrawerForSale(result?.transaction_id).catch(() => {});

                // This sale never went through ensureSplitParent, so nothing
                // has told the kitchen about it. Reachable whenever a backend
                // cold start rejected every per-group parent create and then
                // recovered: a real sale on record, and the same zero-tickets
                // failure this fix exists to close. Guarded, so the ordinary
                // path above — which HAS already dispatched — cannot double.
                await dispatchSplitKitchenTickets(result?.transaction_id, allProcessedProducts);

                // …and nothing has taken its ingredients off the shelf.
                // Guarded the same way, so the ordinary path above — which
                // deducted when it opened its parent — cannot double.
                await linkSplitInventory(result?.transaction_id, allProcessedProducts);
            }

            // **PRINT FINAL CASHIER RECEIPT WITH PAYMENT SUMMARY**
            const tenantInfo = {
                brand_name: brandName,
                currency,
                printable_categories: printableCategories,
                printing: tenantConfig?.printing,
                receipt_header: tenantConfig?.receipt_header,
                receipt_footer: tenantConfig?.receipt_footer,
                receipt_logo: tenantConfig?.receipt_logo,
                // The direct printer path renders from the transaction, so
                // without this id attemptDirectEpsonPrint bails and nothing
                // prints on a network Epson/ESC-POS tenant.
                transaction_id: result?.transaction_id
            };

            // Create payment summary for all payment methods used
            const paymentSummary = [];
            if (totalCashAmount > 0) {
                paymentSummary.push({
                    method: 'Cash',
                    amount: totalCashAmount
                });
            }
            if (totalCardAmount > 0) {
                paymentSummary.push({
                    method: 'Card',
                    amount: totalCardAmount
                });
            }

            const finalCashierReceiptData = {
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                cashier: cashierName,
                paymentMethod: 'Split Payment',
                diningOption: selectedDiningOption,
                tableNumber: tableNumber,
                products: allProcessedProducts,
                subtotal: totalAmount,
                discount: 0,
                serviceCharge: serviceChargeAmount,
                total: totalAmount,
                notes: t('product_split_transaction_note', { count: allProcessedProducts.length, id: result.transaction_id }),
                split_payment_summary: paymentSummary
            };

            console.log('Printing combined cashier receipt with payment summary');
            // Add short delay to ensure tenant info is loaded
            setTimeout(() => {
                printSplitBillReceipts(
                    finalCashierReceiptData,
                    "PRODUCT SPLIT - CASHIER RECEIPT",
                    true,
                    {
                        products: allProcessedProducts,
                        total: totalAmount,
                        subtotal: subtotalAmount,
                        serviceCharge: serviceChargeAmount,
                        payments: paymentSummary
                    },
                    tenantInfo,
                    false  // Don't print customer receipt
                );
            }, 500);

            // **MARK ORDER AS SUBMITTED INSTEAD OF REMOVING**
            if (currentConfirmOrder) {
                await tenantStorage.markOrderSubmitted(currentConfirmOrder, true);
                setPendingOrders(prev => {
                    const newOrders = { ...prev };
                    delete newOrders[currentConfirmOrder];
                    return newOrders;
                });

                setPrintedOrders(prev => {
                    const newPrintedOrders = { ...prev };
                    delete newPrintedOrders[currentConfirmOrder];
                    return newPrintedOrders;
                });

                // The order is settled — stop pointing at it. See the same
                // line in processFinalSplitTransaction for what leaving it
                // set costs.
                setCurrentConfirmOrder(null);
            }

            // Load updated transactions
            await loadTransactions();

            // Reset split state
            setSelectedProductsForSplit([]);
            setAllProcessedProducts([]);
            setTransactionToSplit(null);
            setSplitBillModalOpen(false);
            splitParentRef.current = null;

            setSelectedModalPaymentMethod(null);
            setCashAmount(0);
            setCardAmount(0);

            // Notify parent of completion
            if (onSplitCompleted) {
                onSplitCompleted();
            }

            setSuccess(t('product_split_completed_items', { count: allProcessedProducts.length }));
            setLoading(false);
            endCharge();

            setTimeout(() => {
                setSuccess('');
            }, 1000);

        } catch (err) {
            console.error('Error in processFinalProductSplitTransaction:', err);
            setError(t('failed_to_process_product_split'));
            setLoading(false);
            endCharge();
        }
    };

    const processSplitByNumber = async () => {
        // Prevent multiple executions
        if (loading) {
            console.log('Already processing, skipping...');
            return;
        }

        try {
            console.log('Starting split by number process');

            // Reset payment collection state
            setCustomerPayments([]);
            setCurrentSplitCustomer(1);

            // Open modal for first customer
            setSplitCustomerPaymentModalOpen(true);

            // Close the main split modal
            setSplitByNumberModalOpen(false);

        } catch (err) {
            console.error('Error starting split by number:', err);
            setError('Failed to start split bill process');
        }
    };

    // One customer settling their share of a split-by-customer bill.
    //
    // Was an inline onClick that only pushed the method into React state and
    // printed a slip off `receiptData` alone. Nothing was recorded until the
    // last customer had paid, so on a network Epson tenant — where a receipt
    // is rendered SERVER-SIDE from `transaction_id` — the per-customer slips
    // could not print at all, and the whole bill printed once at the end
    // instead. QC on 9 Sep 2026 caught exactly that: one row, three jobs, no
    // per-customer slip anywhere.
    //
    // Mirrors processIndividualProductPayment: open the sale on the first
    // payment, record every payer as a child of it, keep the money on the
    // parent.
    const processCustomerSplitPayment = async () => {
        // One customer at a time. This path creates real rows now, so a
        // double tap would charge the same share twice.
        if (customerChargeRef.current) return;
        customerChargeRef.current = true;
        try {
            const customerNumber = currentSplitCustomer;
            const method = currentCustomerPaymentMethod;
            const newPayment = { splitNumber: customerNumber, method };
            const updatedPayments = [...customerPayments, newPayment];
            setCustomerPayments(updatedPayments);

            const totalAmount = parseFloat(transactionToSplit.final_amount);
            const splitAmount = totalAmount / numberOfCustomers;

            // The row this customer's slip renders from. Created BEFORE the
            // slip is printed, because the printer renders from the row.
            let childTransactionId = null;
            try {
                const parentId = await ensureCustomerSplitParent();
                const child = await transactionService.createTransaction({
                    transaction_id: '',
                    parent_transaction_id: parentId,
                    split_type: 'table',
                    date: format(new Date(), 'yyyy-MM-dd'),
                    time: format(new Date(), 'HH:mm:ss'),
                    sales_date: await tenantStorage.getState('salesDate') || format(new Date(), 'yyyy-MM-dd'),
                    staff_name: cashierName,
                    payment_method: method,
                    dining_option: selectedDiningOption,
                    table_number: tableNumber,
                    service_charge: transactionToSplit.service_charge,
                    discount_amount: 0,
                    // This payer's share. The parent keeps the bill total.
                    total_amount: splitAmount,
                    final_amount: splitAmount,
                    // Deliberately zero — the parent holds the sale's money.
                    // Four gold aggregations exclude children by filtering on
                    // parent_transaction_id IS NULL; money here would double
                    // the day's revenue the moment that filter moved.
                    cash_amount: 0,
                    card_amount: 0,
                    // Which payer this is, and how many there are. The
                    // receipt renderer needs both to print the agreed share
                    // line, and the count is NOT derivable from the rows:
                    // when customer 1's slip prints, theirs is the only child
                    // that exists yet.
                    split_index: customerNumber,
                    split_count: numberOfCustomers,
                    notes: t('split_customer_of_total', { current: customerNumber, total: numberOfCustomers }),
                    // The whole itemised bill, at full price. The renderer
                    // builds the item block from the row's own items, and the
                    // agreed receipt shows every item and then the share.
                    items: transactionToSplit.items,
                    products: transactionToSplit.products || [],
                    tenant_id: tenantId,
                });
                childTransactionId = child?.transaction_id || null;

                // Never awaited, and never downstream of anything that can
                // throw — see the same call in processIndividualProductPayment.
                kickDrawerForSale(childTransactionId).catch(() => {});

                // Keep the sale's payment mix true after every customer, not
                // only at the end — a split abandoned halfway would otherwise
                // leave a full-bill sale on record with nothing attributed to
                // cash or card.
                const paid = { cash: 0, card: 0 };
                updatedPayments.forEach((payment) => {
                    if (payment.method === 'Cash') paid.cash += splitAmount;
                    else if (payment.method === 'Card') paid.card += splitAmount;
                });
                await transactionService.setSplitTotals(parentId, {
                    cashAmount: paid.cash,
                    cardAmount: paid.card,
                });
            } catch (err) {
                // The customer has already paid in the real world. Losing
                // their slip is bad; refusing to continue the split would be
                // worse, and the sale is still settled at the end.
                console.error('Could not record this split customer, slip will not print:', err);
            }

            const tenantInfo = {
                brand_name: brandName,
                currency,
                printable_categories: printableCategories,
                printing: tenantConfig?.printing,
                receipt_header: tenantConfig?.receipt_header,
                receipt_footer: tenantConfig?.receipt_footer,
                receipt_logo: tenantConfig?.receipt_logo,
                // Without this the direct printer path bails on its
                // identifier check and this customer gets no paper at all.
                transaction_id: childTransactionId
            };

            const receiptData = {
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                cashier: cashierName,
                paymentMethod: method,
                diningOption: selectedDiningOption,
                tableNumber: tableNumber,
                products: transactionToSplit.products || [],
                subtotal: splitAmount,
                discount: 0,
                serviceCharge: 0,
                total: splitAmount,
                notes: t('split_customer_of_total', { current: customerNumber, total: numberOfCustomers }),
                splitInfo: {
                    splitNumber: customerNumber,
                    totalSplits: numberOfCustomers,
                    amountPerSplit: splitAmount.toFixed(2)
                }
            };

            printSplitBillReceipts(
                receiptData,
                `SPLIT ${customerNumber}/${numberOfCustomers} - CUSTOMER RECEIPT`,
                false,
                null,
                tenantInfo
            );

            setSplitCustomerPaymentModalOpen(false);

            if (updatedPayments.length < numberOfCustomers) {
                const nextCustomer = customerNumber + 1;
                setTimeout(() => {
                    setCurrentSplitCustomer(nextCustomer);
                    setSplitCustomerPaymentModalOpen(true);
                }, 300);
            } else {
                setTimeout(() => {
                    processFinalSplitTransaction(updatedPayments);
                }, 500);
            }
        } finally {
            // Re-armed unconditionally: the next customer still has to pay,
            // and a stuck guard would strand the rest of the split.
            customerChargeRef.current = false;
        }
    };

    const processFinalSplitTransaction = async (collectedPayments) => {
        // One charge at a time — see beginCharge.
        if (!beginCharge()) return;
        try {
            setLoading(true);

            const diningConfig = tenantConfig?.dining_options?.[selectedDiningOption];
            const isTableBasedOrder = diningConfig !== undefined && diningConfig !== "";

            if (!isTableBasedOrder) { // Only print for takeaway/non-table orders
                const categoryReceiptData = {
                    date: format(new Date(), 'yyyy-MM-dd'),
                    time: format(new Date(), 'HH:mm:ss'),
                    cashier: cashierName,
                    paymentMethod: 'Split Payment - By Table',
                    diningOption: selectedDiningOption,
                    tableNumber: tableNumber,
                    notes: notes || ''
                };

                const splitPaymentTenantInfo = {
                    brand_name: brandName,
                    currency,
                    printable_categories: printableCategories,
                    printing: tenantConfig?.printing,
                    receipt_header: tenantConfig?.receipt_header,
                    receipt_footer: tenantConfig?.receipt_footer,
                    receipt_logo: tenantConfig?.receipt_logo
                };

                // Print category orders for all products
                const allProducts = transactionToSplit.products || [];
                const categoryOrdersPrinted = printCategoryOrdersOnly(
                    categoryReceiptData,
                    allProducts,
                    splitPaymentTenantInfo
                );

                if (categoryOrdersPrinted) {
                    console.log('Category orders printed for table split payment (takeaway)');
                }
            } else {
                console.log('Skipping category orders - table order already has them printed');
            }
            console.log('Processing final split transaction with payments:', collectedPayments);

            // Add error checking
            if (!transactionToSplit || !transactionToSplit.final_amount) {
                throw new Error('Transaction data is missing or invalid');
            }

            if (!collectedPayments || collectedPayments.length !== numberOfCustomers) {
                throw new Error(`Expected ${numberOfCustomers} payments, got ${collectedPayments?.length || 0}`);
            }

            const totalAmount = parseFloat(transactionToSplit.final_amount);
            const splitAmount = totalAmount / numberOfCustomers;

            let totalCashAmount = 0;
            let totalCardAmount = 0;
            let totalMobileAmount = 0;
            let totalGiftCardAmount = 0;

            collectedPayments.forEach(payment => {
                if (payment.method === 'Cash') {
                    totalCashAmount += splitAmount;
                } else if (payment.method === 'Card') {
                    totalCardAmount += splitAmount;
                } else if (payment.method === 'Mobile') {
                    totalMobileAmount += splitAmount;
                } else if (payment.method === 'Gift Card') {
                    totalGiftCardAmount += splitAmount;
                }
            });

            const transaction = {
                transaction_id: '',
                parent_transaction_id: null,
                split_type: 'table',
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                sales_date: await tenantStorage.getState('salesDate') || format(new Date(), 'yyyy-MM-dd'),
                staff_name: cashierName,
                payment_method: 'Split Payment',
                dining_option: selectedDiningOption,
                table_number: tableNumber,
                service_charge: transactionToSplit.service_charge,
                discount_amount: 0,
                total_amount: totalAmount,
                final_amount: totalAmount,
                notes: t('split_by_table_ways', { ways: numberOfCustomers }),
                items: transactionToSplit.items,
                products: transactionToSplit.products || [],
                cash_amount: totalCashAmount,
                card_amount: totalCardAmount,
                mobile_amount: totalMobileAmount,
                gift_card_amount: totalGiftCardAmount,
                tenant_id: tenantId,
                // See stockAlreadyDeducted.
                stock_already_deducted: stockAlreadyDeducted(),
            };

            // The sale already exists: ensureCustomerSplitParent opened it
            // when the first customer paid, so their slips had a row to
            // render from. Finishing means recording how it was paid, not
            // opening a second sale for the same money. Mirrors
            // processFinalProductSplitTransaction.
            let result;
            if (splitParentRef.current) {
                await transactionService.setSplitTotals(splitParentRef.current, {
                    cashAmount: totalCashAmount,
                    cardAmount: totalCardAmount,
                });
                result = { transaction_id: splitParentRef.current };
                console.log('Split finalised on parent:', result.transaction_id);
            } else {
                // No parent means no customer was recorded — every child
                // creation failed. The sale still has to go on record.
                console.log('Creating transaction:', transaction);
                result = await transactionService.createTransaction(transaction);
                console.log('Transaction created:', result);
                kickDrawerForSale(result?.transaction_id).catch(() => {});

                // Same as processFinalProductSplitTransaction's fallback: a
                // sale that never opened a parent has told the kitchen
                // nothing. Guarded, so the parent path cannot double.
                await dispatchSplitKitchenTickets(result?.transaction_id, transactionToSplit.products);

                // …and told the shelf nothing either. Guarded, so the parent
                // path — which deducted when it opened — cannot double.
                await linkSplitInventory(result?.transaction_id, transactionToSplit.products);
            }

            // **PRINT FINAL CASHIER RECEIPT WITH PAYMENT SUMMARY**
            const tenantInfo = {
                brand_name: brandName,
                currency,
                printable_categories: printableCategories,
                printing: tenantConfig?.printing,
                receipt_header: tenantConfig?.receipt_header,
                receipt_footer: tenantConfig?.receipt_footer,
                receipt_logo: tenantConfig?.receipt_logo,
                // The direct printer path renders from the transaction, so
                // without this id attemptDirectEpsonPrint bails and nothing
                // prints on a network Epson/ESC-POS tenant.
                transaction_id: result?.transaction_id
            };

            const finalCashierReceiptData = {
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                cashier: cashierName,
                paymentMethod: 'Split Payment',
                diningOption: selectedDiningOption,
                tableNumber: tableNumber,
                products: transactionToSplit.products || [],
                subtotal: totalAmount,
                discount: 0,
                serviceCharge: transactionToSplit.service_charge ? (totalAmount * (tenantConfig?.service_charge_rate || 0.12)) : 0,
                total: totalAmount,
                notes: t('split_by_table_transaction_note', { ways: numberOfCustomers, id: result.transaction_id }),
                split_payment_summary: collectedPayments.map(p => ({
                    customer: p.splitNumber,
                    method: p.method,
                    amount: splitAmount
                }))
            };

            console.log('Printing final cashier receipt');
            printSplitBillReceipts(
                finalCashierReceiptData,
                "FINAL SPLIT BILL - CASHIER RECEIPT",
                true,
                {
                    products: transactionToSplit.products || [],
                    total: totalAmount,
                    subtotal: totalAmount,
                    serviceCharge: transactionToSplit.service_charge ? (totalAmount * (tenantConfig?.service_charge_rate || 0.12)) : 0,
                    payments: collectedPayments.map(p => ({
                        method: p.method,
                        amount: splitAmount
                    }))
                },
                tenantInfo,
                false
            );

            // Mark order as submitted using the correct currentConfirmOrder key
            if (currentConfirmOrder) {
                await tenantStorage.markOrderSubmitted(currentConfirmOrder, true);
                setPendingOrders(prev => {
                    const newOrders = { ...prev };
                    delete newOrders[currentConfirmOrder];
                    return newOrders;
                });

                setPrintedOrders(prev => {
                    const newPrintedOrders = { ...prev };
                    delete newPrintedOrders[currentConfirmOrder];
                    return newPrintedOrders;
                });

                // The order is settled — stop pointing at it.
                //
                // `currentConfirmOrder` is the ONLY thing that tells
                // dispatchSplitKitchenTickets "this order's dockets already
                // fired at send-to-kitchen". Every other path that settles a
                // pending order clears it here — transactionFlow's
                // processTransactionWithPayment and handleSplitPaymentSubmit
                // below — but these two split handlers did not, so it stayed
                // set for the rest of the shift and every later split, of
                // any bill, read as "already sent". On staging on 9 Sep 2026
                // that silenced the kitchen for a takeaway six minutes after
                // a table order was split-paid: a real sale, zero
                // order_tracking rows, zero tickets.
                setCurrentConfirmOrder(null);
            }

            // Load updated transactions
            await loadTransactions();

            // Reset state
            setNumberOfCustomers(2);
            setCustomerPayments([]);
            setTransactionToSplit(null);
            splitParentRef.current = null;

            setSelectedModalPaymentMethod(null);
            setCashAmount(0);
            setCardAmount(0);

            // Notify parent of completion
            if (onSplitCompleted) {
                onSplitCompleted();
            }

            setSuccess(t('bill_split_equal_parts_success', { count: numberOfCustomers }));
            setLoading(false);
            endCharge();

            setTimeout(() => {
                setSuccess('');
            }, 1000);

        } catch (err) {
            console.error('Error in processFinalSplitTransaction:', err);
            setError(t('failed_to_process_split_bill'));
            setLoading(false);
            endCharge();
        }
    };

    // Split payment handling
    const handleSplitPaymentSubmit = async () => {
        // One charge at a time — see beginCharge.
        if (!beginCharge()) return;
        try {
            setLoading(true);

            const diningConfig = tenantConfig?.dining_options?.[selectedDiningOption];
            const isTableBasedOrder = diningConfig !== undefined && diningConfig !== "";

            if (!isTableBasedOrder) { // Only print for takeaway/non-table orders
                const categoryReceiptData = {
                    date: format(new Date(), 'yyyy-MM-dd'),
                    time: format(new Date(), 'HH:mm:ss'),
                    cashier: cashierName,
                    paymentMethod: 'Split Payment',
                    diningOption: selectedDiningOption,
                    tableNumber: tableNumber,
                    notes: pendingTransaction?.notes || ''
                };

                const splitPaymentTenantInfo = {
                    brand_name: brandName,
                    currency,
                    printable_categories: printableCategories,
                    printing: tenantConfig?.printing,
                    receipt_header: tenantConfig?.receipt_header,
                    receipt_footer: tenantConfig?.receipt_footer,
                    receipt_logo: tenantConfig?.receipt_logo
                };

                // Print category orders first
                const categoryOrdersPrinted = printCategoryOrdersOnly(
                    categoryReceiptData,
                    selectedProducts || [],
                    splitPaymentTenantInfo
                );

                if (categoryOrdersPrinted) {
                    console.log('Category orders printed for split payment (takeaway)');
                }
            } else {
                console.log('Skipping category orders - table order already has them printed');
            }
            const { subtotal, serviceCharge, total } = handleCalculateTotals();

            if (splitAmountValue(cashAmount) + splitAmountValue(cardAmount) !== parseFloat(total)) {
                setError(t('cash_card_amounts_must_sum_total'));
                setLoading(false);
                endCharge();
                return;
            }

            const parentTransaction = {
                ...pendingTransaction,
                payment_method: 'Split Payment',
                cash_amount: splitAmountValue(cashAmount),
                card_amount: splitAmountValue(cardAmount),
                notes: `${pendingTransaction?.notes ? pendingTransaction.notes + ' - ' : ''}${t('parent_transaction_split_payment')}`,
                // See stockAlreadyDeducted. Set explicitly rather than left
                // to the spread, so all five creates state it the same way.
                stock_already_deducted: stockAlreadyDeducted(),
            };

            const parentResult = await transactionService.createTransaction(parentTransaction);
            kickDrawerForSale(parentResult?.transaction_id).catch(() => {});

            // This path never goes through ensureSplitParent — it creates its
            // own row above — so it needs its own dispatch, or a cash-and-card
            // split stays exactly as invisible to the kitchen as the other two
            // split types were.
            await dispatchSplitKitchenTickets(parentResult?.transaction_id, selectedProducts);

            // …and its own deduction, for the same reason. A cash-and-card
            // split carries no split_type at all, so it is invisible even to
            // a query for split sales — and it sold its ingredients free too.
            await linkSplitInventory(parentResult?.transaction_id, selectedProducts);

            setSplitPaymentModalOpen(false);

            const receiptData = {
                date: format(new Date(), 'yyyy-MM-dd'),
                time: format(new Date(), 'HH:mm:ss'),
                cashier: cashierName,
                paymentMethod: 'Split Payment',
                diningOption: selectedDiningOption,
                tableNumber: tableNumber,
                products: selectedProducts || [], // Ensure products is an array
                subtotal: subtotal,
                discount: 0, // Assuming no discount in split payment
                serviceCharge: serviceCharge,
                total: total,
                notes: pendingTransaction?.notes || '',
                split_payment: {
                    cash_amount: splitAmountValue(cashAmount),
                    card_amount: splitAmountValue(cardAmount)
                }
            };

            const tenantInfo = {
                brand_name: brandName,
                currency,
                printable_categories: printableCategories,
                printing: tenantConfig?.printing,
                receipt_header: tenantConfig?.receipt_header,
                receipt_footer: tenantConfig?.receipt_footer,
                receipt_logo: tenantConfig?.receipt_logo,
                // The direct printer path renders from the transaction, so
                // without this id attemptDirectEpsonPrint bails and nothing
                // prints on a network Epson/ESC-POS tenant.
                transaction_id: parentResult?.transaction_id
            };

            // Add debug logging
            console.log('Receipt Data for Split Payment:', receiptData);
            console.log('Products array:', receiptData.products);
            console.log('Tenant Info:', tenantInfo);

            // Create all three receipts as one print job
            const receiptsToprint = [];

            // 1. Customer receipt
            receiptsToprint.push({
                data: receiptData,
                title: "CUSTOMER RECEIPT",
                isForCashier: false
            });

            // 2. Cash cashier receipt (if cash amount > 0)
            if (splitAmountValue(cashAmount) > 0) {
                const cashReceiptData = {
                    ...receiptData,
                    paymentMethod: 'Cash',
                    total: splitAmountValue(cashAmount),
                    subtotal: splitAmountValue(cashAmount),
                    notes: t('cash_portion_amount', { currency, amount: splitAmountValue(cashAmount).toFixed(2) })
                };
                receiptsToprint.push({
                    data: cashReceiptData,
                    title: "CASH RECEIPT - CASHIER",
                    isForCashier: true
                });
            }

            // 3. Card cashier receipt (if card amount > 0)
            if (splitAmountValue(cardAmount) > 0) {
                const cardReceiptData = {
                    ...receiptData,
                    paymentMethod: 'Card',
                    total: splitAmountValue(cardAmount),
                    subtotal: splitAmountValue(cardAmount),
                    notes: t('card_portion_amount', { currency, amount: splitAmountValue(cardAmount).toFixed(2) })
                };
                receiptsToprint.push({
                    data: cardReceiptData,
                    title: "CARD RECEIPT - CASHIER",
                    isForCashier: true
                });
            }

            // Print all receipts as one combined job
            const allReceiptsHTML = receiptsToprint.map(receipt =>
                `<div class="print-page" style="display: flex; justify-content: center; align-items: center; always;">
                    <div style="text-align: center;">
                        ${generateReceiptHTML(receipt.data, receipt.title, receipt.isForCashier, tenantInfo)}
                    </div>
                </div>`
            ).join('');

            // config + role were both missing here, so on a network
            // Epson/ESC-POS tenant attemptDirectEpsonPrint returned on its
            // first line ("no role given") and the sale printed nothing at
            // all — the HTML above only ever reaches a browser/Electron
            // tenant. Confirmed on tenant_demo: the split's receipt job sat
            // queued with fetched_at null while normal sales completed.
            printWithIframe(
                allReceiptsHTML,
                "SPLIT PAYMENT RECEIPTS",
                tenantConfig?.printing?.method,
                tenantInfo,
                'receipt',
            );

            if (currentConfirmOrder && pendingOrders[currentConfirmOrder]) {
                // Mark order as submitted ONLY for actual pending orders
                await tenantStorage.markOrderSubmitted(currentConfirmOrder, true);

                // Remove from local state
                setPendingOrders(prev => {
                    const newOrders = { ...prev };
                    delete newOrders[currentConfirmOrder];
                    return newOrders;
                });

                setPrintedOrders(prev => {
                    const newPrintedOrders = { ...prev };
                    delete newPrintedOrders[currentConfirmOrder];
                    return newPrintedOrders;
                });

                setCurrentConfirmOrder(null);
            }

            setTimeout(() => {
                loadTransactions();

                // Notify parent of completion
                if (onSplitCompleted) {
                    onSplitCompleted();
                }

                setCashAmount(0);
                setCardAmount(0);
                setPendingTransaction(null);
                setSelectedModalPaymentMethod(null);
                setSuccess(t('split_payment_processed_successfully'));
                setLoading(false);
                endCharge();

                setTimeout(() => {
                    setSuccess('');
                }, 1000);
            }, 1000);

        } catch (err) {
            setError(t('failed_to_process_split_payment'));
            console.error(err);
            setLoading(false);
            endCharge();
        }
    };

    // Expose split functions to parent through ref
    React.useImperativeHandle(ref, () => ({
        handleSplitByProducts,
        handleSplitByNumber,
        handleSplitByMethod,
        showSplitTypeModal
    }), [handleSplitByProducts, handleSplitByNumber, handleSplitByMethod, showSplitTypeModal]);

    return (
        <>
            {/* Split Bill by Products Modal */}
            <Modal
                isOpen={splitBillModalOpen}
                onClose={() => setSplitBillModalOpen(false)}
                title={t('split_bill_by_products')}
                footer={(
                    <>
                        <button
                            type="button"
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            onClick={() => setSplitBillModalOpen(false)}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="button"
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            onClick={() => {
                                if (selectedProductsForSplit.length === 0) return;

                                // Set current product info for the modal
                                if (selectedProductsForSplit.length === 1) {
                                    // Single product
                                    setCurrentProductForSplit(selectedProductsForSplit[0]);
                                } else {
                                    // Multiple products - create a summary object
                                    const totalAmount = splitSubtotal(selectedProductsForSplit);
                                    setCurrentProductForSplit({
                                        name: t('selected_items_count', { count: selectedProductsForSplit.length }),
                                        selling_price: totalAmount
                                    });
                                }

                                setProductSplitPaymentModalOpen(true);
                            }}
                            disabled={selectedProductsForSplit.length === 0}
                        >
                            {t('process_selected_products')}
                        </button>
                    </>
                )}
            >
                {transactionToSplit && (
                    <div className="space-y-4">
                        <p>{t('select_products_individually')}</p>

                        <div className="mt-4 border-t pt-4">
                            <h4 className="font-medium mb-2">{t('products')}:</h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {transactionToSplit.products ?
                                    // **EXPAND PRODUCTS TO SHOW INDIVIDUALLY**
                                    transactionToSplit.products.flatMap((product, idx) =>
                                        Array.from({ length: product.quantity }, (_, qtyIdx) => ({
                                            ...product,
                                            uniqueId: `${product.product_code}-${qtyIdx}`,
                                            quantity: 1,
                                            originalIndex: idx
                                        }))
                                    ).map((individualProduct, idx) => {
                                        const isSelected = selectedProductsForSplit.some(p => p.uniqueId === individualProduct.uniqueId);
                                        return (
                                            <div key={individualProduct.uniqueId} className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id={`product-${individualProduct.uniqueId}`}
                                                    checked={isSelected}
                                                    onChange={() => toggleProductForSplit(individualProduct)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <label htmlFor={`product-${individualProduct.uniqueId}`} className="ml-2 block text-sm text-gray-700">
                                                    {individualProduct.name} - {currency}{individualProduct.selling_price.toFixed(2)}
                                                </label>
                                            </div>
                                        );
                                    })
                                    :
                                    // Handle items format
                                    transactionToSplit.items && transactionToSplit.items.flatMap((item, idx) =>
                                        Array.from({ length: parseInt(item.quantity) }, (_, qtyIdx) => ({
                                            product_code: item.product_code,
                                            uniqueId: `${item.product_code}-${qtyIdx}`,
                                            name: item.product_name,
                                            category: item.category,
                                            quantity: 1,
                                            selling_price: parseFloat(item.unit_price),
                                            originalIndex: idx
                                        }))
                                    ).map((individualProduct, idx) => {
                                        const isSelected = selectedProductsForSplit.some(p => p.uniqueId === individualProduct.uniqueId);
                                        return (
                                            <div key={individualProduct.uniqueId} className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id={`product-${individualProduct.uniqueId}`}
                                                    checked={isSelected}
                                                    onChange={() => toggleProductForSplit(individualProduct)}
                                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                                />
                                                <label htmlFor={`product-${individualProduct.uniqueId}`} className="ml-2 block text-sm text-gray-700">
                                                    {translateDynamic(individualProduct.name, t)} - {currency}{individualProduct.selling_price.toFixed(2)}
                                                </label>
                                            </div>
                                        );
                                    })
                                }
                            </div>

                            <div className="mt-4 border-t pt-4">
                                <div className="flex justify-between">
                                    <span>{t('selected_subtotal')}:</span>
                                    <span>
                                        {currency}{splitSubtotal(selectedProductsForSplit).toFixed(2)}
                                    </span>
                                </div>
                                {transactionToSplit.service_charge && (
                                    <div className="flex justify-between">
                                        <span>{t('service_charge')} ({Math.round((tenantConfig?.service_charge_rate || 0.12) * 100)}%):</span>
                                        <span>
                                            {currency}{(splitSubtotal(selectedProductsForSplit) * (tenantConfig?.service_charge_rate || 0.12)).toFixed(2)}
                                        </span>
                                    </div>
                                )}
                                {splitTaxRate() > 0 && (
                                    <div className="flex justify-between">
                                        <span>{t('tax')} ({Math.round(splitTaxRate() * 100)}%):</span>
                                        <span>
                                            {currency}{(splitSubtotal(selectedProductsForSplit) * splitTaxRate()).toFixed(2)}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between font-medium">
                                    <span>{t('selected_total')}:</span>
                                    <span>
                                        {/* Was subtotal × service charge only, so the cashier was
                                            shown £20.00 for what the charge below made £25.60. */}
                                        {currency}{splitTotals(selectedProductsForSplit, transactionToSplit.service_charge).total.toFixed(2)}
                                    </span>
                                </div>
                                <div className="text-sm text-gray-600 mt-2">
                                    {t('selected')}: {selectedProductsForSplit.length} {t('items')}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Split Payment Modal */}
            <Modal
                isOpen={splitPaymentModalOpen}
                onClose={() => setSplitPaymentModalOpen(false)}
                title={t('split_payment_cash_card')}
                footer={(
                    <>
                        <button
                            type="button"
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            onClick={() => setSplitPaymentModalOpen(false)}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="button"
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            onClick={handleSplitPaymentSubmit}
                            data-testid="split-process-payment"
                            disabled={charging}
                        >
                            {charging ? t('processing_payment') : t('process_payment')}
                        </button>
                    </>
                )}
            >
                <div className="space-y-4">
                    <p>{t('enter_amount_by_method')} {currency} {handleCalculateTotals().total.toFixed(2)}.</p>

                    <div className="mt-4">
                        <div className="mb-4">
                            <label htmlFor="cashAmount" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('cash_amount')} ({currency})
                            </label>
                            <input
                                type="number"
                                id="cashAmount"
                                min="0"
                                step="0.01"
                                value={cashAmount}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    const total = handleCalculateTotals().total;
                                    // Keep the raw string so the field can be
                                    // cleared. Coercing to a number here wrote
                                    // `0` straight back into the input, so a
                                    // cashier could not backspace past it to
                                    // type a fresh amount.
                                    setCashAmount(raw);
                                    const cashValue = parseFloat(raw) || 0;
                                    setCardAmount(Math.max(0, (total - cashValue).toFixed(2)));
                                }}
                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            />
                        </div>

                        <div className="mb-4">
                            <label htmlFor="cardAmount" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('card_amount')} ({currency})
                            </label>
                            <input
                                type="number"
                                id="cardAmount"
                                min="0"
                                step="0.01"
                                value={cardAmount}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    const total = handleCalculateTotals().total;
                                    // See the cash field above — the raw string
                                    // is held so the input can be emptied.
                                    setCardAmount(raw);
                                    const cardValue = parseFloat(raw) || 0;
                                    setCashAmount(Math.max(0, (total - cardValue).toFixed(2)));
                                }}
                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            />
                        </div>

                        <div className="bg-gray-50 p-4 rounded-md">
                            <div className="flex justify-between font-medium">
                                <span>{t('total')}:</span>
                                <span>{currency}{handleCalculateTotals().total.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm mt-2">
                                <span>{t('sum_of_payments')}:</span>
                                <span className={splitAmountValue(cashAmount) + splitAmountValue(cardAmount) !== handleCalculateTotals().total ? 'text-red-600 font-bold' : ''}>
                                    {currency}{(splitAmountValue(cashAmount) + splitAmountValue(cardAmount)).toFixed(2)}
                                </span>
                            </div>
                            {splitAmountValue(cashAmount) + splitAmountValue(cardAmount) !== handleCalculateTotals().total && (
                                <div className="text-red-600 text-sm mt-1">
                                    {t('amounts_must_sum_total')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Split Bill by Table Modal */}
            <Modal
                isOpen={splitByNumberModalOpen}
                onClose={() => setSplitByNumberModalOpen(false)}
                title={t('split_by_table')}
                footer={(
                    <>
                        <button
                            type="button"
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            onClick={() => setSplitByNumberModalOpen(false)}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="button"
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            onClick={() => {
                                if (!loading) {
                                    processSplitByNumber();
                                }
                            }}
                            disabled={numberOfCustomers < 2 || loading}
                        >
                            {loading ? t('processing') : t('process_split')}
                        </button>
                    </>
                )}
            >
                {transactionToSplit && (
                    <div className="space-y-4">
                        <p>{t('split_bill_equally')}.</p>

                        <div className="mt-4">
                            <label htmlFor="numberOfCustomers" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('number_of_customers')}
                            </label>
                            <input
                                type="number"
                                id="numberOfCustomers"
                                min="2"
                                max="20"
                                value={numberOfCustomers}
                                onChange={(e) => setNumberOfCustomers(Math.max(2, parseInt(e.target.value) || 2))}
                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                            />
                        </div>

                        <div className="mt-4 border-t pt-4">
                            <h4 className="font-medium mb-2">{t('bill_summary')}:</h4>
                            <div className="space-y-1">
                                <div className="flex justify-between">
                                    <span>{t('total_bill_amount')}:</span>
                                    <span>{currency}{parseFloat(transactionToSplit.final_amount).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between font-medium">
                                    <span>{t('amount_per_customer')}:</span>
                                    <span>{currency}{(parseFloat(transactionToSplit.final_amount) / numberOfCustomers).toFixed(2)}</span>
                                </div>
                            </div>

                            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                                <p className="text-sm text-yellow-800">
                                    {t('this_will_create')} {numberOfCustomers} {t('separate_receipts_equal')}
                                    {t('each_receipt_printed')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Split Type Modal */}
            <Modal
                isOpen={splitTypeModalOpen}
                onClose={() => {
                    setSplitTypeModalOpen(false);
                    // Don't automatically reopen payment modal
                }}
                title={t('select_split_payment_type')}
                footer={(
                    <button
                        type="button"
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        onClick={() => setSplitTypeModalOpen(false)}
                    >
                        {t('cancel')}
                    </button>
                )}
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600">{t('select_split_payment_how')}:</p>

                    <div className="grid grid-cols-1 gap-3 mt-4">
                        <button
                            onClick={() => {
                                setSplitTypeModalOpen(false);
                                handleSplitByProducts(pendingTransaction);
                            }}
                            className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            {t('split_by_product')}
                        </button>
                        <button
                            onClick={() => {
                                setSplitTypeModalOpen(false);
                                handleSplitByNumber(pendingTransaction);
                            }}
                            className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            {t('split_by_table')}
                        </button>
                        <button
                            onClick={() => {
                                setSplitTypeModalOpen(false);
                                handleSplitByMethod();
                            }}
                            className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            {t('split_by_payment_method')}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Split Customer Payment Method Modal */}
            <Modal
                isOpen={splitCustomerPaymentModalOpen}
                onClose={() => setSplitCustomerPaymentModalOpen(false)}
                title={`${t('select_payment_method_customer')} ${currentSplitCustomer}`}
                footer={(
                    <>
                        <button
                            type="button"
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            onClick={() => setSplitCustomerPaymentModalOpen(false)}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="button"
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            onClick={() => processCustomerSplitPayment()}
                            data-testid="split-customer-confirm"
                            disabled={charging}
                        >
                            {t('confirm')}
                        </button>
                    </>
                )}
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-700">
                        {t('how_would_customer')} {currentSplitCustomer} of {numberOfCustomers} {t('like_to_pay')}
                    </p>

                    <div className="mt-4">
                        <div className="grid grid-cols-2 gap-3">
                            {paymentMethods.slice(0, 4).map(method => (
                                <button
                                    key={method}
                                    onClick={() => setCurrentCustomerPaymentMethod(method)}
                                    className={`flex items-center justify-center px-4 py-3 border ${currentCustomerPaymentMethod === method
                                        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50'
                                        : 'border-gray-300 bg-white hover:bg-gray-50'
                                        } rounded-md shadow-sm text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
                                >
                                    {translateDynamic(method, t)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-4 bg-gray-50 p-3 rounded-md">
                        <h4 className="font-medium text-sm mb-1">{t('split_summary')}:</h4>
                        <p className="text-sm">
                            {t('amount_per_customer')}: {currency}{transactionToSplit &&
                                (parseFloat(transactionToSplit.final_amount) / numberOfCustomers).toFixed(2)
                            }
                        </p>
                    </div>
                </div>
            </Modal>

            {/* Product Split Payment Method Modal */}
            <Modal
                isOpen={productSplitPaymentModalOpen}
                onClose={() => setProductSplitPaymentModalOpen(false)}
                title={`${t('select_payment_method_dash')} ${currentProductForSplit?.name || t('product')}`}
                footer={(
                    <>
                        <button
                            type="button"
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            onClick={() => setProductSplitPaymentModalOpen(false)}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="button"
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            onClick={() => processIndividualProductPayment()}
                            data-testid="split-product-confirm"
                            disabled={charging}
                        >
                            {t('confirm')}
                        </button>
                    </>
                )}
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-700">
                        {t('how_pay_for')} {currentProductForSplit?.name}?
                    </p>
                    <p className="text-sm font-medium">
                        {t('amount')}: {currency}{currentProductForSplit?.selling_price?.toFixed(2)}
                    </p>

                    <div className="mt-4">
                        <div className="grid grid-cols-2 gap-3">
                            {paymentMethods.slice(0, 4).map(method => (
                                <button
                                    key={method}
                                    onClick={() => setCurrentProductPaymentMethod(method)}
                                    className={`flex items-center justify-center px-4 py-3 border ${currentProductPaymentMethod === method
                                        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50'
                                        : 'border-gray-300 bg-white hover:bg-gray-50'
                                        } rounded-md shadow-sm text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
                                >
                                    {translateDynamic(method, t)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
});

export default SplitBillManager;